// src/components/Discover/DiscoverDetail.js
import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { db } from '../../services/firebase';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import './DiscoverDetail.css';

const DiscoverDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveItem, isSaved, updateDigitalCardFromInteraction } = useContext(UserContext);
  
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleItems, setVisibleItems] = useState([]);
  const [currentItemInView, setCurrentItemInView] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const containerRef = useRef(null);
  const itemRefs = useRef({});
  const observerRef = useRef(null);
  
  // ADDED: Normalize image path function (copied from DiscoverWithFilter)
  const normalizeImagePath = useCallback((imagePath) => {
    if (!imagePath) return null;
    
    // Handle absolute URLs (starting with http/https)
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // Handle relative paths
    const cleanPath = imagePath.replace(/^\/+/, '');
    return `/${cleanPath}`;
  }, []);
  
  // License and attribution utilities
  const getLicenseBySource = (source) => {
    if (!source) return { name: 'Unknown', requiresAttribution: false };
    
    const sourceLower = source?.toLowerCase() || '';
    if (sourceLower.includes('pixabay')) return { name: 'Pixabay License', requiresAttribution: false };
    if (sourceLower.includes('met')) return { name: 'Met Open Access', requiresAttribution: false };
    if (sourceLower.includes('smithsonian')) return { name: 'Smithsonian Open Access', requiresAttribution: false };
    if (sourceLower.includes('user:')) return { name: 'User Generated', requiresAttribution: true };
    
    return { name: 'Unknown', requiresAttribution: false };
  };
  
  const getAttributionText = (item) => {
    if (!item) return '';
    
    const license = getLicenseBySource(item.source);
    if (!license.requiresAttribution) return '';
    
    let attribution = item.creator ? `By ${item.creator}` : '';
    if (item.source) attribution += `${attribution ? ' | ' : ''}Source: ${item.source}`;
    
    return attribution;
  };

  // Update URL when item comes into view
  const updateCurrentItem = useCallback((item) => {
    if (currentItemInView !== item.id) {
      setCurrentItemInView(item.id);
      window.history.replaceState(null, '', `/discover/${item.id}`);
    }
  }, [currentItemInView]);

  // Simple scroll handler for sidebar
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const scrollTop = containerRef.current.scrollTop;
      
      // Show sidebar after scrolling past 300px
      if (scrollTop > 300 && !showSidebar) {
        setShowSidebar(true);
      } else if (scrollTop <= 100 && showSidebar) {
        setShowSidebar(false);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [showSidebar]);

  // Intersection observer to track which item is in view
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const itemId = entry.target.dataset.itemId;
            const item = allItems.find(item => item.id === itemId);
            if (item) {
              updateCurrentItem(item);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '-20% 0px -20% 0px'
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [allItems, updateCurrentItem]);

  // Observe visible items
  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) return;

    const currentRefs = { ...itemRefs.current };
    
    Object.values(currentRefs).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(currentRefs).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [visibleItems]);
  
  // OPTIMIZED: Load all items using JSON fallback for iOS performance
  useEffect(() => {
    const fetchAllItems = async () => {
      setIsLoading(true);
      try {
        // Skip Firebase - go straight to JSON fallback for faster iOS loading
        throw new Error('Using JSON fallback for faster iOS loading');
        
      } catch (err) {
        console.log('🔄 Loading fallback data from discover.json...');
        
        // Fallback to JSON file when Firebase fails/times out
        try {
          const response = await fetch('/discover.json');
          const jsonData = await response.json();
          
          console.log(`📁 Loaded ${jsonData.entries?.length || jsonData.length} items from discover.json fallback`);
          
          // Process JSON data to match expected format (same as DiscoverWithFilter)
          const processedJsonItems = (jsonData.entries || jsonData || []).map(item => ({
            ...item,
            image: normalizeImagePath(item.imageUrl || item.image_url),
            imageUrl: item.imageUrl || item.image_url
          }));

          const startIndex = processedJsonItems.findIndex(item => item.id === id);
          if (startIndex === -1) throw new Error('Item not found');
          
          setAllItems(processedJsonItems);
          setCurrentItemInView(id);
          
          const batchSize = 10;
          const startBatch = Math.max(0, startIndex - 2);
          const endBatch = Math.min(processedJsonItems.length, startIndex + batchSize);
          const initialBatch = processedJsonItems.slice(startBatch, endBatch);
          setVisibleItems(initialBatch);
          
          setError(null); // Clear error since fallback worked
          
        } catch (jsonError) {
          console.error('❌ Both Firebase and JSON failed:', jsonError);
          setError('Failed to load content from both Firebase and local data');
          setAllItems([]);
        }
        
        setIsLoading(false);
      }
    };
    
    fetchAllItems();
  }, [id, normalizeImagePath]);

  // Scroll to the clicked item after items are loaded
  useEffect(() => {
    if (visibleItems.length > 0 && id && itemRefs.current[id]) {
      setTimeout(() => {
        const targetElement = itemRefs.current[id];
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'instant', 
            block: 'center' 
          });
        }
      }, 100);
    }
  }, [visibleItems, id]);
  
  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollHeight = container.scrollHeight;
      
      if (scrollPosition >= scrollHeight - 1000) {
        const lastVisibleIndex = allItems.findIndex(item => item.id === visibleItems[visibleItems.length - 1]?.id);
        const nextBatchStart = lastVisibleIndex + 1;
        const nextBatch = allItems.slice(nextBatchStart, nextBatchStart + 5);
        
        if (nextBatch.length > 0) {
          setVisibleItems(prev => [...prev, ...nextBatch]);
        }
      }

      if (container.scrollTop < 1000 && visibleItems.length > 0) {
        const firstVisibleIndex = allItems.findIndex(item => item.id === visibleItems[0]?.id);
        if (firstVisibleIndex > 0) {
          const prevBatchEnd = firstVisibleIndex;
          const prevBatchStart = Math.max(0, firstVisibleIndex - 5);
          const prevBatch = allItems.slice(prevBatchStart, prevBatchEnd);
          
          if (prevBatch.length > 0) {
            setVisibleItems(prev => [...prevBatch, ...prev]);
          }
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [visibleItems, allItems]);
  
  // Handle save/like action
  const handleSave = async (item) => {
    if (!item) return;
    
    try {
      if (saveItem) {
        await saveItem(item.id);
        
        if (updateDigitalCardFromInteraction) {
          updateDigitalCardFromInteraction(item, 'like');
        }
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };
  
  // Handle share
  const handleShare = async (item) => {
    const url = `${window.location.origin}/discover/${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      // Could add a toast notification here
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  // ENHANCED: Advanced similarity scoring algorithm based on universal search scoring
  const getRelatedItems = useCallback(() => {
    if (!currentItemInView || !allItems.length) return [];
    
    const currentItem = allItems.find(item => item.id === currentItemInView);
    if (!currentItem) return [];

    console.log('🔍 Computing similarity for:', currentItem.title);

    // Calculate similarity scores for all other items
    const scoredItems = allItems
      .filter(item => item.id !== currentItemInView)
      .map(item => {
        let score = 0;
        const matchedFactors = [];

        // 1. Same Creator/Artist (highest weight - 15 points)
        if (currentItem.creator && item.creator && 
            currentItem.creator.toLowerCase() === item.creator.toLowerCase()) {
          score += 15;
          matchedFactors.push('creator');
        }

        // 2. Same Source (high weight - 12 points)
        if (currentItem.source && item.source && 
            currentItem.source.toLowerCase() === item.source.toLowerCase()) {
          score += 12;
          matchedFactors.push('source');
        }

        // 3. Shared Signals (high weight - 10 points each, max 30)
        let signalMatches = 0;
        if (currentItem.signals && item.signals) {
          currentItem.signals.forEach(signal => {
            if (item.signals.some(itemSignal => 
                itemSignal.toLowerCase() === signal.toLowerCase())) {
              score += 10;
              signalMatches++;
              if (signalMatches === 1) matchedFactors.push('signals');
            }
          });
          // Cap signal bonus at 30 points
          if (signalMatches > 3) {
            score -= (signalMatches - 3) * 10;
          }
        }

        // 4. Shared Categories (medium-high weight - 8 points each, max 24)
        let categoryMatches = 0;
        if (currentItem.categories && item.categories) {
          currentItem.categories.forEach(category => {
            if (item.categories.some(itemCat => 
                itemCat.toLowerCase() === category.toLowerCase())) {
              score += 8;
              categoryMatches++;
              if (categoryMatches === 1) matchedFactors.push('categories');
            }
          });
          // Cap category bonus at 24 points
          if (categoryMatches > 3) {
            score -= (categoryMatches - 3) * 8;
          }
        }

        // 5. Shared Tags (medium weight - 6 points each, max 30)
        let tagMatches = 0;
        if (currentItem.tags && item.tags) {
          currentItem.tags.forEach(tag => {
            if (item.tags.some(itemTag => 
                itemTag.toLowerCase() === tag.toLowerCase())) {
              score += 6;
              tagMatches++;
              if (tagMatches === 1) matchedFactors.push('tags');
            }
          });
          // Cap tag bonus at 30 points
          if (tagMatches > 5) {
            score -= (tagMatches - 5) * 6;
          }
        }

        // 6. Title/Description Similarity (medium weight - 5 points each match)
        const currentTitle = (currentItem.title || '').toLowerCase();
        const currentDesc = (currentItem.description || '').toLowerCase();
        const itemTitle = (item.title || '').toLowerCase();
        const itemDesc = (item.description || '').toLowerCase();

        // Extract meaningful words (filter out common words)
        const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'among', 'since', 'until', 'under', 'over'];
        
        const currentWords = [...currentTitle.split(/\s+/), ...currentDesc.split(/\s+/)]
          .filter(word => word.length > 3 && !commonWords.includes(word));
        
        const itemWords = [...itemTitle.split(/\s+/), ...itemDesc.split(/\s+/)]
          .filter(word => word.length > 3 && !commonWords.includes(word));

        let textMatches = 0;
        currentWords.forEach(word => {
          if (itemWords.some(itemWord => itemWord.includes(word) || word.includes(itemWord))) {
            score += 5;
            textMatches++;
            if (textMatches === 1) matchedFactors.push('text');
          }
        });

        // 7. Time Proximity Bonus (small bonus for items created around the same time)
        if (currentItem.createdAt && item.createdAt) {
          const timeDiff = Math.abs(
            (currentItem.createdAt.seconds || 0) - (item.createdAt.seconds || 0)
          );
          const daysDiff = timeDiff / (24 * 60 * 60);
          
          if (daysDiff < 7) {
            score += 3; // Same week
            if (!matchedFactors.includes('timing')) matchedFactors.push('timing');
          } else if (daysDiff < 30) {
            score += 2; // Same month
            if (!matchedFactors.includes('timing')) matchedFactors.push('timing');
          } else if (daysDiff < 90) {
            score += 1; // Same quarter
            if (!matchedFactors.includes('timing')) matchedFactors.push('timing');
          }
        }

        return {
          ...item,
          similarityScore: score,
          matchedFactors: matchedFactors,
          debugInfo: {
            signalMatches,
            categoryMatches,
            tagMatches,
            textMatches
          }
        };
      })
      .filter(item => item.similarityScore > 5) // Only include items with meaningful similarity
      .sort((a, b) => {
        // Primary sort: similarity score (descending)
        if (b.similarityScore !== a.similarityScore) {
          return b.similarityScore - a.similarityScore;
        }
        
        // Secondary sort: prefer items with more match types
        if (b.matchedFactors.length !== a.matchedFactors.length) {
          return b.matchedFactors.length - a.matchedFactors.length;
        }
        
        // Tertiary sort: prefer more recent items
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      })
      .slice(0, 12); // Return top 12 most similar items

    console.log('✅ Top similar items:', scoredItems.slice(0, 3).map(item => ({
      title: item.title,
      score: item.similarityScore,
      factors: item.matchedFactors
    })));

    return scoredItems;
  }, [currentItemInView, allItems]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading content...</p>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="detail-error">
        <h2>Content not found</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/discover')} className="action-button">Return to Discover</button>
      </div>
    );
  }

  const relatedItems = getRelatedItems();
  
  return (
    <div className="discover-detail-container">
      <div className="detail-content">
        <div className="detail-main" ref={containerRef}>
          <div className="feed-container">
            {visibleItems.map((item) => (
              <div 
                key={item.id} 
                className="feed-item"
                data-item-id={item.id}
                ref={el => itemRefs.current[item.id] = el}
              >
                <div className="feed-item-image-container">
                  {/* FIXED: Use the normalized image path like DiscoverWithFilter */}
                  <img 
                    src={item.image || '/placeholder-image.jpg'} 
                    alt={item.title || 'Discover content'} 
                    className="feed-item-image"
                    onError={(e) => {
                      console.log(`⚠️ Image failed to load: ${item.id}`, { 
                        attempted: item.image, 
                        fallback: item.imageUrl 
                      });
                      
                      // Try the original imageUrl as fallback
                      if (item.imageUrl && e.target.src !== item.imageUrl) {
                        e.target.src = item.imageUrl;
                      } else {
                        e.target.src = '/placeholder-image.jpg';
                      }
                    }}
                  />
                </div>
                
                <div className="feed-item-info">
                  <div className="feed-item-header">
                    <div className="item-details">
                      <h2 className="item-title">{item.title || 'Untitled'}</h2>
                      {item.source && (
                        <div className="item-source">{item.source}</div>
                      )}
                      {item.creator && (
                        <div className="item-creator">by {item.creator}</div>
                      )}
                    </div>
                    
                    <div className="item-actions">
                      <button 
                        className={`save-button ${isSaved && isSaved(item.id) ? 'saved' : ''}`}
                        onClick={() => handleSave(item)}
                        aria-label={isSaved && isSaved(item.id) ? 'Unsave' : 'Save'}
                      >
                        {isSaved && isSaved(item.id) ? '❤️' : '🤍'}
                      </button>
                      <button 
                        className="share-button"
                        onClick={() => handleShare(item)}
                        aria-label="Share"
                      >
                        🔗
                      </button>
                    </div>
                  </div>
                  
                  {item.description && (
                    <p className="item-description">{item.description}</p>
                  )}
                  
                  {getAttributionText(item) && (
                    <p className="item-attribution">{getAttributionText(item)}</p>
                  )}
                  
                  <p className="license-info">{getLicenseBySource(item.source).name}</p>
                  
                  {item.tags && item.tags.length > 0 && (
                    <div className="item-tags">
                      {item.tags.slice(0, 6).map((tag, tagIndex) => (
                        <span key={tagIndex} className="category-tag">{tag}</span>
                      ))}
                      {item.tags.length > 6 && (
                        <span className="category-tag more-tags">+{item.tags.length - 6} more</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Sidebar - positioned exactly like DiscoverWithFilter */}
        <div className="detail-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-header">
              <img src="/assets-branding/artifex-banner.png" alt="Artifex" className="artifex-logo" />
            </div>
            
            <div className="suggested-header">
              <h3>Suggested for you</h3>
              <Link to="/discover" className="see-all-link">
                See All
              </Link>
            </div>
            
            <div className="suggested-accounts">
              {relatedItems.slice(0, 3).map((relItem) => (
                <div key={relItem.id} className="suggested-account">
                  <Link to={`/discover/${relItem.id}`} className="account-avatar">
                    {/* FIXED: Use normalized image path for related items too */}
                    <img 
                      src={relItem.image || '/placeholder-image.jpg'} 
                      alt={relItem.title || 'Related content'} 
                      className="avatar-image"
                      onError={(e) => {
                        if (relItem.imageUrl && e.target.src !== relItem.imageUrl) {
                          e.target.src = relItem.imageUrl;
                        } else {
                          e.target.src = '/placeholder-image.jpg';
                        }
                      }}
                    />
                  </Link>
                  <div className="account-info">
                    <Link to={`/discover/${relItem.id}`} className="account-name">
                      {relItem.title?.length > 20 
                        ? `${relItem.title.substring(0, 20)}...` 
                        : relItem.title || 'Untitled'}
                    </Link>
                    <span className="account-source">
                      {relItem.source || 'Unknown source'}
                    </span>
                    {/* ENHANCED: Show similarity info in development */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="similarity-debug" style={{ fontSize: '10px', color: '#666' }}>
                        Score: {relItem.similarityScore} | {relItem.matchedFactors.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="sidebar-section">
            <div className="related-content">
              <h3>More like this</h3>
              <div className="related-grid">
                {relatedItems.slice(3).map((relItem) => (
                  <Link 
                    key={relItem.id} 
                    to={`/discover/${relItem.id}`} 
                    className="related-item"
                    title={`${relItem.title} (Score: ${relItem.similarityScore})`}
                  >
                    {/* FIXED: Use normalized image path for related grid too */}
                    <img 
                      src={relItem.image || '/placeholder-image.jpg'} 
                      alt={relItem.title || 'Related content'} 
                      className="related-image"
                      onError={(e) => {
                        if (relItem.imageUrl && e.target.src !== relItem.imageUrl) {
                          e.target.src = relItem.imageUrl;
                        } else {
                          e.target.src = '/placeholder-image.jpg';
                        }
                      }}
                    />
                    {/* ENHANCED: Show similarity score on hover in development */}
                    {process.env.NODE_ENV === 'development' && (
                      <div className="similarity-score" style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '2px 4px',
                        fontSize: '10px',
                        borderRadius: '2px'
                      }}>
                        {relItem.similarityScore}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="sidebar-footer">
            <div className="footer-links">
              <Link to="/about">About</Link>
              <Link to="/help">Help</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
            </div>
            <div className="footer-copyright">
              © Artifex {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverDetail;